import { useCallback, useEffect, useRef, useState } from "react";
import contactsDirectoryApi from "../../../api/contactsDirectoryApi";
import { useContacts } from "../contexts/ContactsContext";

/**
 * Hook phân trang cho tab "Lịch sử kết bạn".
 * Mỗi trang load 1 request tới `/contacts-directory/friend-requests` với
 * page/limit truyền thẳng, KHÔNG accumulate qua các trang.
 */
export const useFriendHistoryPaginated = ({ pageSize = 50 } = {}) => {
  const { selectedShopIds, search } = useContacts();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Reset về trang 1 khi shop đổi hoặc search đổi
  useEffect(() => {
    setPage(1);
  }, [selectedShopIds, search]);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    if (!selectedShopIds.length) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const data = await contactsDirectoryApi.getFriendRequests(
        {
          shopIds: selectedShopIds,
          direction: "all",
          page,
          limit: pageSize,
        },
        ctrl.signal,
      );
      let list = Array.isArray(data?.items) ? data.items : [];

      // Client-side search (trên trang hiện tại)
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter((r) => {
          const name = (
            r.partner?.name ||
            r.partner?.zaloName ||
            ""
          ).toLowerCase();
          const phone =
            r.partner?.phone ||
            (Array.isArray(r.partner?.phoneList)
              ? r.partner.phoneList[0]
              : "") ||
            "";
          return name.includes(q) || String(phone).includes(q);
        });
      }

      setItems(list);
      setTotal(data?.total || 0);
    } catch (e) {
      if (e.name !== "CanceledError" && e.name !== "AbortError") {
        setError(e.message || "Lỗi tải danh sách");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedShopIds, page, pageSize, search]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goToPage = useCallback(
    (next) => {
      const clamped = Math.min(Math.max(1, next), totalPages);
      setPage(clamped);
    },
    [totalPages],
  );

  return {
    items,
    total,
    totalPages,
    page,
    pageSize,
    loading,
    error,
    reload: load,
    goToPage,
    hasPrev: page > 1,
    hasNext: page < totalPages,
  };
};

export default useFriendHistoryPaginated;
